import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface HoverTooltipProps {
  /** Tooltip text shown on hover / focus of the trigger. */
  text: string;
  /** Optional class on the trigger wrapper. */
  className?: string;
  /** The trigger element(s). */
  children: React.ReactNode;
  /**
   * Optional trigger handlers. The wrapper already is the single interactive
   * element (`role="button"`), so a caller that needs a clickable/hoverable
   * tooltip trigger passes them here instead of nesting another button.
   */
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Which side the bubble opens to. Default `bottom`. */
  placement?: 'top' | 'bottom';
}

/**
 * Small hover/focus tooltip rendered through a portal with `position: fixed`, so
 * it is never clipped by an ancestor's `overflow` (e.g. a scrollable list).
 * Mirrors the pattern used by `ContentMasterOtherSourcesBadge`.
 */
const HoverTooltip: React.FC<HoverTooltipProps> = ({
  text,
  className,
  children,
  onClick,
  onMouseEnter,
  onMouseLeave,
  placement = 'bottom',
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const open = useCallback(() => {
    const el = triggerRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    // `top`: anchor at the trigger's top edge - the bubble is pulled fully
    // above it by the `--top` modifier's transform.
    setCoords({
      top: placement === 'top' ? rect.top - 8 : rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  }, [placement]);

  const close = useCallback(() => setCoords(null), []);

  return (
    <span
      ref={triggerRef}
      className={className}
      tabIndex={0}
      role="button"
      aria-label={text}
      onMouseEnter={() => {
        open();
        onMouseEnter?.();
      }}
      onMouseLeave={() => {
        close();
        onMouseLeave?.();
      }}
      onFocus={open}
      onBlur={close}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {children}
      {coords &&
        createPortal(
          <span
            className={placement === 'top' ? 'hover-tooltip hover-tooltip--top' : 'hover-tooltip'}
            role="tooltip"
            style={{ top: coords.top, left: coords.left }}
          >
            {text}
          </span>,
          document.body
        )}
    </span>
  );
};

export default HoverTooltip;
